/**
 * Connections (Listing Management) translations — RSX-5749.
 *
 * Covers the "Connections" section of Listing Management:
 *   - Connections list, filtered by status (RsPartialConnections.vue, connections/Index.vue)
 *   - Connection detail page (connections/single/Index.vue)
 *   - Connection info tabs (RsPartialConnectionInfo.vue)
 *   - Request Connection modal (RsModalRequestConnection.vue)
 *
 * French strings are machine-drafted (pending human review), mirroring the
 * approach used for `listing-management.ts` in Part 1 (RSX-5695).
 *
 * DB-sourced copy/images (partner + promoted_listing_types) are localized
 * separately via `*_fr` columns + a frontend locale picker — not here.
 */

export const connectionsEn = {
  connections: {
    list: {
      title: 'Connections',
      // Status filter above the single merged list, replacing the old "Available
      // Connections" / "My Connections" split that listed the same connection under both.
      // A connection has exactly one status, but "Connected" deliberately also lists the
      // ones with an upgrade outstanding, so the tab counts overlap by design.
      statusFilter: {
        connected: 'Connected ({count})',
        upgradeAvailable: 'Upgrade available ({count})',
        available: 'Available ({count})',
      },
      statusPill: {
        connected: 'Connected',
        upgradeAvailable: 'Upgrade available',
        available: 'Available',
      },
      infoBanner: 'Add available Rental Listing Sites to get more leads. Add promoted listings to your properties by enabling promoted connections by clicking \'Connect Now\'. Contact your Rentsync account manager for more information.',
      tags: 'Tags',
      allPlaceholder: 'All',
      removeFilters: 'Remove Filters',
      new: '*NEW*',
      promotedConnectionDetailsSuffix: ' Promoted Connection Details',
      enable: 'Enable',
      enableStandardBanner: 'Enable standard connection to unlock promoted connection.',
      enableStandardConnection: 'Enable Standard Connection',
      requestPromotedListings: 'Request Promoted Listings',
      requested: 'Requested',
      listingTypes: {
        standard: 'Standard Listings',
        promoted: 'Promoted Listings',
      },
      tabs: {
        termsAndConditions: 'Terms & Conditions',
        networkExposureScale: 'Network Exposure Scale',
        listingCards: 'Listing Cards',
        packageOptions: 'Package Options',
      },
      pills: {
        new: 'New',
        free: 'Free',
        promo: 'Promo',
        externalSubscription: 'External Subscription',
      },
      status: {
        andJoiner: ' and ',
        // Appended to a status phrase only when there are tiers to name, so each locale
        // owns its own punctuation around the list.
        typesSeparator: ' : ',
        standardAvailable: 'Standard Connection Available',
        standardAndPromotedAvailable: 'Standard & <b>Promoted</b> Connection(s) Available{types}',
        promotedAvailable: '<b>Promoted</b> Connection(s) Available{types}',
        standardAndPromotedRequested: 'Standard & <b>Promoted</b> Connection(s) Requested{types}',
        promotedRequested: '<b>Promoted</b> Connection(s) Requested{types}',
        standardActive: 'Standard Active',
        standardAndPromotedActive: 'Standard & Promoted <b>Connection(s)</b> Active{types}',
        activeList: '{types} Active',
        requestedItem: '{name} Requested',
      },
      toastRequested: 'Promoted Connection Requested! | Promoted Connections Requested!',
      toastRequestFailed: 'Unable to request promoted connections. Please try again.',
    },

    single: {
      viewMediaKit: 'View Media Kit',
      aboutTheService: 'About the Service',
      aboutCompany: 'About {name}',
      pricing: 'Pricing',
      connect: 'Connect {name}',
      externalSubscription: 'EXTERNAL SUBSCRIPTION',
      activateNow: 'Activate Now!',
      alreadyConnected: 'Looks like you\'re already connected!',
      deactivate: 'Deactivate',
      cannotDeactivateTitle: 'Connection cannot be deactivated!',
      cannotDeactivateMessage: 'You still have properties turned on for this connection. Manage property advertising connections on the main Ad Syndication page!',
      promotedAvailableTitle: 'Promoted Listings are available for this Rental Listing Site',
      unableToDeactivate: 'Unable to deactivate connection',
      removeTitle: 'Remove this connection?',
      removeText: 'This connection will be removed from your advertising panel!',
      // HubSpot form IDs for the "Connect <partner>" sign-up form (portal 4888868,
      // region na1). Field labels and radio options live in the form definition in
      // HubSpot, so the French version is a separate form and the ID is translated.
      formId: {
        connect: 'b3a8e8bf-5898-4301-a63d-bd06cc64ff7c',
        fbCatalog: 'f0b66d0a-6796-4783-82d1-c0dd2edcd878',
      },
    },

    info: {
      terms: {
        essentialsHeading: 'Essentials & Essentials +',
        essentialsBody: 'Essentials and Essentials + listings require a minimum 60 day commitment. Listings will only show in the top 3 results if there are only 3 Essential listings purchased in that geographic area. If more than 3 are purchased, Zumper’s algorithm will determine ranking and rotate listings in the top three slots based on renter search behaviour.',
        essentialsBody2: 'Results may vary from property to property due to unique factors such as property, location, and pricing, determining the total number of leads received.',
        zPostsHeading: 'zPosts',
        zPostsBody: 'zPosts requires a minimum 6 month commitment. The property listing must continue to be promoted to either Essentials or Essentials + throughout the duration of the 6-month commitment to zPosts. The Essential listing and zPost must be applied to the same property. A Google My Business page must already be set up. Rentsync does not support this setup.',
        pricingHeading: 'Pricing',
        pricingBody: 'The cost to upgrade to Essentials is in addition to standard listing costs. The cost to upgrade to Essentials + is in addition to standard listing costs and not in addition to the Essentials listings costs. The cost to add-on zPosts is in addition to the cost of an Essentials or Essentials + listing. All rates quoted are exclusive of taxes and are subject to applicable regional taxes.',
        cancellationHeading: 'Cancellation',
        cancellationBody: 'Once the minimum commitment period has been met, written notice must be provided to Rentsync to cancel a property. Properties that are promoted will continue to be promoted until written confirmation is received.',
      },
      listingCardsTitle: 'Listing Cards',
      listingCardsBody1: 'See your listing cards placed above others based on your chosen promotional package.',
      listingCardsBody2: 'Your visibility is increased with unique card features such as exclusive tags, featured tags, 3D tour tags, and increased listing card sizes including a large carousel for renters to browse property photos.',
      listingCardsCaption: 'The example shown represents listing card style & placement on Rentals.ca; card style will vary depending on the listing site.',
      packageOptionsTitle: 'Package Options',
      packageOptionsBody: 'Our team will help you choose a package that is the perfect fit for <b>your</b> portfolio!',
      exposureScaleAlt: 'Rentals Infinity Network Exposure Scale Graphics',
      listingCardsAlt: 'Rentals Infinity Listing Cards Graphics',
      packageOptionsAlt: 'Rentals Infinity Network Package Options',
      defaultDisclaimer: '*Promoted listings require a minimum 60 day commitment. The cost to upgrade to a Promoted listing is in addition to standard listing costs. All rates quoted above are exclusive of taxes and are subject to applicable regional taxes. Results may vary from property to property due to unique factors such as property, location, and pricing, determining the total number of leads received.',
      perPropertyMonth: '/property/month*',
      priceBracket: {
        one: '1 Listing',
        range: '1 - {max} Listings',
        from: '{min} - {max} Listings',
        plus: '{min}+ Listings',
      },
    },

    request: {
      title: 'Request Connection: {name}',
      processing: 'Processing...',
      request: 'Request',
      viewCatalogue: 'View Catalogue',
      catalogueUrl: 'https://rs-assets.rentsync.com/platform-assets/rentals-infinity-bundle-catalogue.pdf',
      pricingLabel: 'Pricing - ',
      standard: 'Standard',
      perContract: 'As per contract, terms and conditions apply.',
      zStoriesTooltip: 'Social media-style interactive content available in both Zumper and PadMapper native iOS apps, boost leads by 18% avg.',
      maxExposure: 'For maximum advertising exposure, your Rentsync account manager will set up a time to discuss details.',
      // eslint-disable-next-line no-template-curly-in-string -- vue-i18n interpolation, not a template literal
      perPropertyMonth: '${amount}/property/month*',
      minDuration: '{days} days minimum duration',
      unlockDelay: 'Please allow up to 2 business days to unlock promoted listings on {name}.',
      requestConnectionHeading: 'Request Connection',
      standardRequiredTooltip: 'A Standard connection is required in order to enable Essentials and Essentials +',
      selectConnections: 'Select which connections you would like to add to your Rentsync account. (Select all that apply)',
      contactManager: 'Contact your Rentsync account manager or click on the \'Help\' button if you have questions or need more information.',
      consent: 'I agree that I have verified all the information on the page and authorize Rentsync to add these new billing charges to my account effective immediately.',
      priceBracket: {
        one: '1 Promoted Listing',
        range: '1 - {max} Promoted Listings',
        from: '{min} - {max} Promoted Listings',
        plus: '{min}+ Promoted Listings',
      },
    },
  },
} as const;

export const connectionsFr = {
  connections: {
    list: {
      title: 'Connexions',
      statusFilter: {
        connected: 'Connectées ({count})',
        upgradeAvailable: 'Amélioration possible ({count})',
        available: 'Disponibles ({count})',
      },
      statusPill: {
        connected: 'Connectée',
        upgradeAvailable: 'Amélioration possible',
        available: 'Disponible',
      },
      infoBanner: 'Ajoutez des sites d\'annonces locatives disponibles pour obtenir plus de prospects. Ajoutez des annonces mises en avant à vos propriétés en activant les connexions mises en avant en cliquant sur « Connecter maintenant ». Communiquez avec votre gestionnaire de compte Rentsync pour plus d\'information.',
      tags: 'Étiquettes',
      allPlaceholder: 'Tous',
      removeFilters: 'Supprimer les filtres',
      new: '*NOUVEAU*',
      promotedConnectionDetailsSuffix: ' Détails de la connexion mise en avant',
      enable: 'Activer',
      enableStandardBanner: 'Activez la connexion standard pour débloquer la connexion mise en avant.',
      enableStandardConnection: 'Activer la connexion standard',
      requestPromotedListings: 'Demander des annonces mises en avant',
      requested: 'Demandé',
      listingTypes: {
        standard: 'Annonces standard',
        promoted: 'Annonces mises en avant',
      },
      tabs: {
        termsAndConditions: 'Conditions générales',
        networkExposureScale: 'Échelle d\'exposition du réseau',
        listingCards: 'Fiches d\'annonce',
        packageOptions: 'Options de forfait',
      },
      pills: {
        new: 'Nouveau',
        free: 'Gratuit',
        promo: 'Promo',
        externalSubscription: 'Abonnement externe',
      },
      status: {
        andJoiner: ' et ',
        typesSeparator: ' : ',
        standardAvailable: 'Connexion standard disponible',
        standardAndPromotedAvailable: 'Connexion(s) standard et <b>mise(s) en avant</b> disponible(s){types}',
        promotedAvailable: 'Connexion(s) <b>mise(s) en avant</b> disponible(s){types}',
        standardAndPromotedRequested: 'Connexion(s) standard et <b>mise(s) en avant</b> demandée(s){types}',
        promotedRequested: 'Connexion(s) <b>mise(s) en avant</b> demandée(s){types}',
        standardActive: 'Standard actif',
        standardAndPromotedActive: 'Connexion(s) standard et <b>mise(s) en avant</b> active(s){types}',
        activeList: '{types} actif',
        requestedItem: '{name} demandé',
      },
      toastRequested: 'Connexion mise en avant demandée! | Connexions mises en avant demandées!',
      toastRequestFailed: 'Impossible de demander les connexions mises en avant. Veuillez réessayer.',
    },

    single: {
      viewMediaKit: 'Voir la trousse média',
      aboutTheService: 'À propos du service',
      aboutCompany: 'À propos de {name}',
      pricing: 'Tarification',
      connect: 'Connecter {name}',
      externalSubscription: 'ABONNEMENT EXTERNE',
      activateNow: 'Activer maintenant!',
      alreadyConnected: 'Il semble que vous soyez déjà connecté!',
      deactivate: 'Désactiver',
      cannotDeactivateTitle: 'La connexion ne peut pas être désactivée!',
      cannotDeactivateMessage: 'Vous avez encore des propriétés activées pour cette connexion. Gérez les connexions publicitaires des propriétés sur la page principale de diffusion des annonces!',
      promotedAvailableTitle: 'Des annonces mises en avant sont disponibles pour ce site d\'annonces locatives',
      unableToDeactivate: 'Impossible de désactiver la connexion',
      removeTitle: 'Retirer cette connexion?',
      removeText: 'Cette connexion sera retirée de votre panneau publicitaire!',
      // Identifiants des formulaires HubSpot pour le formulaire d'inscription
      // « Connecter <partenaire> » (portail 4888868, région na1). Les libellés des champs
      // et des options proviennent de la définition du formulaire dans HubSpot : la version
      // française est donc un formulaire distinct et l'identifiant est traduit.
      formId: {
        connect: 'bffdca9f-41d8-4049-b543-bfac71a30f7d',
        // Pas de version française du formulaire catalogue Facebook : l'anglais s'affiche.
        fbCatalog: 'f0b66d0a-6796-4783-82d1-c0dd2edcd878',
      },
    },

    info: {
      terms: {
        essentialsHeading: 'Essentials et Essentials +',
        essentialsBody: 'Les annonces Essentials et Essentials + exigent un engagement minimal de 60 jours. Les annonces n\'apparaîtront dans les 3 premiers résultats que s\'il n\'y a que 3 annonces Essentials achetées dans cette zone géographique. Si plus de 3 sont achetées, l\'algorithme de Zumper déterminera le classement et fera alterner les annonces dans les trois premières positions en fonction du comportement de recherche des locataires.',
        essentialsBody2: 'Les résultats peuvent varier d\'une propriété à l\'autre en raison de facteurs uniques tels que la propriété, l\'emplacement et le prix, qui déterminent le nombre total de prospects reçus.',
        zPostsHeading: 'zPosts',
        zPostsBody: 'zPosts exige un engagement minimal de 6 mois. L\'annonce de la propriété doit continuer à être mise en avant en Essentials ou Essentials + pendant toute la durée de l\'engagement de 6 mois envers zPosts. L\'annonce Essentials et le zPost doivent être appliqués à la même propriété. Une page Google My Business doit déjà être configurée. Rentsync ne prend pas en charge cette configuration.',
        pricingHeading: 'Tarification',
        pricingBody: 'Le coût de la mise à niveau vers Essentials s\'ajoute aux coûts des annonces standard. Le coût de la mise à niveau vers Essentials + s\'ajoute aux coûts des annonces standard et non aux coûts des annonces Essentials. Le coût de l\'ajout de zPosts s\'ajoute au coût d\'une annonce Essentials ou Essentials +. Tous les tarifs indiqués excluent les taxes et sont assujettis aux taxes régionales applicables.',
        cancellationHeading: 'Annulation',
        cancellationBody: 'Une fois la période d\'engagement minimale atteinte, un avis écrit doit être transmis à Rentsync pour annuler une propriété. Les propriétés mises en avant continueront de l\'être jusqu\'à la réception d\'une confirmation écrite.',
      },
      listingCardsTitle: 'Fiches d\'annonce',
      listingCardsBody1: 'Voyez vos fiches d\'annonce placées au-dessus des autres selon le forfait promotionnel choisi.',
      listingCardsBody2: 'Votre visibilité est accrue grâce à des caractéristiques de fiche uniques telles que des étiquettes exclusives, des étiquettes en vedette, des étiquettes de visite 3D et des formats de fiche agrandis, y compris un grand carrousel permettant aux locataires de parcourir les photos de la propriété.',
      listingCardsCaption: 'L\'exemple présenté illustre le style et le placement des fiches d\'annonce sur Rentals.ca; le style des fiches variera selon le site d\'annonces.',
      packageOptionsTitle: 'Options de forfait',
      packageOptionsBody: 'Notre équipe vous aidera à choisir le forfait parfaitement adapté à <b>votre</b> portefeuille!',
      exposureScaleAlt: 'Graphiques de l\'échelle d\'exposition du réseau Rentals Infinity',
      listingCardsAlt: 'Graphiques des fiches d\'annonce Rentals Infinity',
      packageOptionsAlt: 'Options de forfait du réseau Rentals Infinity',
      defaultDisclaimer: '*Les annonces mises en avant exigent un engagement minimal de 60 jours. Le coût de la mise à niveau vers une annonce mise en avant s\'ajoute aux coûts des annonces standard. Tous les tarifs indiqués ci-dessus excluent les taxes et sont assujettis aux taxes régionales applicables. Les résultats peuvent varier d\'une propriété à l\'autre en raison de facteurs uniques tels que la propriété, l\'emplacement et le prix, qui déterminent le nombre total de prospects reçus.',
      perPropertyMonth: '/propriété/mois*',
      priceBracket: {
        one: '1 annonce',
        range: '1 à {max} annonces',
        from: '{min} à {max} annonces',
        plus: '{min}+ annonces',
      },
    },

    request: {
      title: 'Demander une connexion : {name}',
      processing: 'Traitement en cours...',
      request: 'Demander',
      viewCatalogue: 'Voir le catalogue',
      catalogueUrl: 'https://rs-assets.rentsync.com/platform-assets/rentals-infinity-bundle-catalogue.pdf',
      pricingLabel: 'Tarification - ',
      standard: 'Standard',
      perContract: 'Selon le contrat; des conditions générales s\'appliquent.',
      zStoriesTooltip: 'Contenu interactif de style médias sociaux disponible dans les applications iOS natives de Zumper et PadMapper, augmentant les prospects de 18 % en moyenne.',
      maxExposure: 'Pour une exposition publicitaire maximale, votre gestionnaire de compte Rentsync organisera une rencontre pour discuter des détails.',
      perPropertyMonth: '{amount} $/propriété/mois*',
      minDuration: 'durée minimale de {days} jours',
      unlockDelay: 'Veuillez prévoir jusqu\'à 2 jours ouvrables pour débloquer les annonces mises en avant sur {name}.',
      requestConnectionHeading: 'Demander une connexion',
      standardRequiredTooltip: 'Une connexion standard est requise pour activer Essentials et Essentials +',
      selectConnections: 'Sélectionnez les connexions que vous souhaitez ajouter à votre compte Rentsync. (Sélectionnez toutes les options applicables)',
      contactManager: 'Communiquez avec votre gestionnaire de compte Rentsync ou cliquez sur le bouton « Aide » si vous avez des questions ou besoin de plus d\'information.',
      consent: 'Je confirme avoir vérifié toutes les informations de la page et j\'autorise Rentsync à ajouter ces nouveaux frais de facturation à mon compte avec effet immédiat.',
      priceBracket: {
        one: '1 annonce mise en avant',
        range: '1 à {max} annonces mises en avant',
        from: '{min} à {max} annonces mises en avant',
        plus: '{min}+ annonces mises en avant',
      },
    },
  },
} as const;
